import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Bookmark, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "";
  }
}

/**
 * Sidebar list of previously-saved calculator runs for the given `calcType`.
 * Clicking an item calls `onLoad(entry)` so the parent can hydrate the form.
 * The parent should bump `refreshToken` after saving a new entry to force a reload.
 */
export default function CalcHistoryPanel({ calcType, onLoad, refreshToken = 0, renderSummary }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/calc-history", { params: { calc_type: calcType } });
      setItems(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [calcType]);

  useEffect(() => { load(); }, [load, refreshToken]);

  const onDelete = async (id) => {
    try {
      await api.delete(`/calc-history/${id}`);
      setItems((prev) => prev.filter((e) => e.id !== id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-none" data-testid={`calc-history-${calcType}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <Bookmark className="w-3.5 h-3.5 text-[#0D9488]" />
          <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-600">
            My Catalog · {items.length}
          </div>
        </div>
      </div>
      <div className="p-3 space-y-2 max-h-[520px] overflow-y-auto">
        {loading && (
          <div className="text-slate-400 text-[11px] font-mono-num uppercase tracking-widest px-1 py-4 text-center">Loading…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="text-slate-400 text-[11px] font-mono-num uppercase tracking-widest px-1 py-4 text-center">
            No saved runs yet
          </div>
        )}
        {items.map((e) => (
          <div
            key={e.id}
            className="border border-slate-200 hover:border-slate-300 transition-colors bg-slate-50/60 p-2.5 group"
            data-testid={`calc-history-item-${e.id}`}
          >
            <div className="flex items-baseline justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-slate-900 text-sm font-medium truncate">
                  {e.label || (calcType === "peptide" ? "Untitled peptide run" : "Nutrition targets")}
                </div>
                <div className="font-mono-num text-[10px] text-slate-500 uppercase tracking-widest mt-0.5">
                  {fmtDate(e.created_at)}
                </div>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  type="button"
                  onClick={() => onLoad?.(e)}
                  className="w-7 h-7 flex items-center justify-center text-slate-500 hover:text-[#0D9488] border border-slate-200 hover:border-[#0D9488]"
                  aria-label="Load into calculator"
                  data-testid={`calc-history-load-${e.id}`}
                  title="Load into calculator"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(e.id)}
                  className="w-7 h-7 flex items-center justify-center text-slate-500 hover:text-[#FF3B30] border border-slate-200 hover:border-[#FF3B30]"
                  aria-label="Delete"
                  data-testid={`calc-history-delete-${e.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            {renderSummary && (
              <div className="mt-2 pt-2 border-t border-slate-200 font-mono-num text-[11px] text-slate-500">
                {renderSummary(e)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
